import { useAccountCrumbs } from '@thd-olt-component-react/customer-account-navigation';
import React, { useEffect } from 'react';
import { Typography, Button } from '@one-thd/sui-atomic-components';
import { AxiosError, AxiosResponse } from 'axios';
import PageLayout from '../components/shared/PageLayout';
import { SubscriptionProps } from '../models/subscriptions';
import { getWindow } from '../utils/general';
import { getOrderSubscriptionHMAC } from '../api/oms/order-subscriptions';

interface IOrderSubsciprtionHMACResponse {
  clientToken: string;
  timestamp: number;
  clientId: string;
  ogHmacToken: string;
  ogTimestamp: number;
  ogClientId: string;
}

// Merchant ID for OG
const MERCHANT_ID = '379dc8f09a4311e7806bbc764e106cf4';

export const Subscriptions = (props: SubscriptionProps) => {
  const { customerType, customerRole } = props;

  const crumbs = useAccountCrumbs([
    {
      label: 'Subscriptions',
      path: '/myaccount/ordersubscription',
    },
  ]);

  useEffect(() => {
    // OG_URL must be read here at runtime, not at module load time,
    // to ensure window.__EXPERIENCE_CONTEXT__ is available (SSR / hydration timing).
    const ogUrl = getWindow()?.__EXPERIENCE_CONTEXT__?.hosts?.orderGroove;
    const scriptSrc = `${ogUrl}/${MERCHANT_ID}/msi.js`;

    // Preload the OG script immediately so the browser downloads it in parallel
    // with the HMAC API call, reducing the total time before the widget renders.
    const preloadLink = document.createElement('link');
    preloadLink.rel = 'preload';
    preloadLink.as = 'script';
    preloadLink.href = scriptSrc;
    document.head.appendChild(preloadLink);

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = scriptSrc;

    let scriptAppended = false;

    getOrderSubscriptionHMAC()
      .then((response: AxiosResponse<IOrderSubsciprtionHMACResponse>) => {
        const { ogTimestamp, ogClientId, ogHmacToken } = response.data;
        document.cookie = `og_auth=${ogClientId}|${ogTimestamp}|${ogHmacToken}; path=/; secure`;
      })
      .catch((error: AxiosError) => {
        // eslint-disable-next-line no-console
        console.debug('Error getting HMAC sub token', error);
      })
      .finally(() => {
        document.body.appendChild(script);
        scriptAppended = true;
      });

    return () => {
      document.head.removeChild(preloadLink);
      if (scriptAppended) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <PageLayout
      crumbs={crumbs}
      pageTitle="Subscriptions"
      customerRole={customerRole}
      customerType={customerType}
      internalRoutes={['/myaccount/ordersubscription']}
    >
      <div className="sui-flex sui-flex-col sui-max-w-[1039px]">
        <div>
          <div className="sui-p-[10px]">
            <Typography
              variant="h1"
              className="sui-uppercase sui-font-extrabold"
            >
              Subscriptions
            </Typography>
          </div>
          <div className="sui-py-[10px] sui-px-[10px]">
            <Typography>
              You can manage, modify, and track all of your subscription orders
              below.
            </Typography>
          </div>
        </div>
        <div className="sui-p-[10px] md:sui-width-[335px] sm:sui-width-[335px]">
          <Button
            variant="primary"
            href="https://www.homedepot.com/c/subscription_service?cm_sp=vanity-_-subscriptions-_-JAN19"
          >
            <span className="sui-font-extrabold">Shop Subscriptions</span>
          </Button>
        </div>
        <div id="og-msi" />
      </div>
    </PageLayout>
  );
};
Subscriptions.displayName = 'Subscriptions';
